"use client";

import AppImage from "@/components/ui/AppImage";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

export default function AboutHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="pt-32 pb-0 relative overflow-hidden bg-dark-bg">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          y,
          opacity,
          background: "radial-gradient(ellipse 70% 60% at 50% 0%, rgba(75, 0, 130, 0.15) 0%, rgba(106, 13, 173, 0.1) 40%, transparent 70%)"
        }} />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          className="max-w-3xl mb-16"
          variants={staggerContainer}
          initial="initial"
          animate="animate">
          
          <motion.span variants={fadeInUp} className="section-label mb-6 inline-flex">Our Story</motion.span>
          <motion.h1
            variants={fadeInUp}
            className="font-heading font-extrabold text-text-primary mb-6"
            style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "-0.025em", lineHeight: "1.05" }}>
            
            We built Helium because we were{" "}
            <span className="gradient-text">frustrated customers</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl text-text-secondary leading-relaxed">
            After running ecommerce brands and dealing with Chinese 3PLs that couldn't communicate, missed deadlines, and offered zero transparency — we moved to Shenzhen and built the partner we always wanted.
          </motion.p>
        </motion.div>

        {/* Hero image */}
        <motion.div
          className="rounded-4xl overflow-hidden h-72 md:h-96 relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          style={{ y: imageY }}>
          
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_135ea089a-1764637482598.png"
            alt="Modern logistics warehouse with organized shelving and workers fulfilling orders in Shenzhen"
            fill
            className="object-cover" />
          
          <div className="absolute inset-0 bg-gradient-to-r from-deep-purple/60 to-transparent" />
          <div className="absolute bottom-8 left-8 text-white">
            <div className="text-xs font-bold tracking-widest uppercase opacity-70 mb-1 font-heading">Our Warehouse</div>
            <div className="text-xl font-bold font-heading">Shenzhen, Guangdong Province</div>
          </div>
        </motion.div>
      </div>
    </section>);


}